import { useState, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { triggerUserTradeRefresh, RefreshRequest } from '../services/api';
import { useCacheCoordinator } from './useCacheCoordinator';

// Hook return type
interface UseSmartTradeRefresherReturn {
  triggerSmartRefresh: (request?: RefreshRequest) => Promise<{ success: boolean; hadChanges: boolean; newTrades: number }>;
  isFetching: boolean;
  lastRefreshTime: Date | null;
  refreshCount: number;
}

/**
 * Smart trade refresher that only invalidates cache when new trades are detected
 * This prevents unnecessary cache invalidation and maintains performance benefits
 */
export const useSmartTradeRefresher = (): UseSmartTradeRefresherReturn => {
  const queryClient = useQueryClient();
  const { invalidateTradeRelatedData } = useCacheCoordinator();
  const [isFetching, setIsFetching] = useState(false);
  const [lastRefreshTime, setLastRefreshTime] = useState<Date | null>(null);
  const [refreshCount, setRefreshCount] = useState(0);

  /**
   * Enhanced refresh function that coordinates backend refresh with cache invalidation
   * Only invalidates cache when new trades are actually detected
   */
  const triggerSmartRefresh = useCallback(async (request: RefreshRequest = {}) => {
    if (isFetching) {
      console.log('🔄 Refresh already in progress, skipping...');
      return { success: false, hadChanges: false, newTrades: 0 };
    }

    setIsFetching(true);
    setRefreshCount(prev => prev + 1);

    try {
      console.log('🔄 Starting smart trade refresh...', request);

      // Use the enhanced API endpoint
      const refreshResult = await triggerUserTradeRefresh(request);
      
      setLastRefreshTime(new Date());

      console.log('✅ Refresh completed:', {
        success: refreshResult.success,
        changesDetected: refreshResult.changes_detected,
        tradeCount: refreshResult.trade_count,
        cacheInvalidated: refreshResult.cache_invalidated,
        message: refreshResult.message
      });

      // Enhanced cache invalidation using cache coordinator
      if (refreshResult.changes_detected) {
        console.log('🔄 Invalidating cache due to detected changes using cache coordinator...');
        
        try {
          // Use cache coordinator for comprehensive invalidation
          await invalidateTradeRelatedData('new_trades', {
            strategy: 'aggressive', // Invalidate all related data including dashboard
            includeRelated: true,
            refetchActive: true
          });
          
          console.log('✅ Cache coordinator invalidation completed');
        } catch (coordinatorError) {
          console.error('❌ Cache coordinator invalidation failed, falling back to manual:', coordinatorError);
          
          // Fallback to manual invalidation if coordinator fails
          await Promise.all([
            queryClient.invalidateQueries({ queryKey: ['closedTrades'] }),
            queryClient.invalidateQueries({ queryKey: ['tradeSymbols'] }),
            queryClient.invalidateQueries({ queryKey: ['tradeAccounts'] }),
            queryClient.invalidateQueries({ queryKey: ['tradeStrategies'] }),
            queryClient.invalidateQueries({ queryKey: ['uncoveredTrades'] }),
            // Add dashboard-specific invalidations
            queryClient.invalidateQueries({ queryKey: ['accountSummary'] }),
            queryClient.invalidateQueries({ queryKey: ['strategySummary'] }),
            queryClient.invalidateQueries({ queryKey: ['pnlTimeframes'] }),
            queryClient.invalidateQueries({ queryKey: ['accountDaily'] }),
            queryClient.invalidateQueries({ queryKey: ['monthlyCalendar'] }),
          ]);
        }
      } else if (refreshResult.cache_invalidated) {
        console.log('ℹ️ Backend already handled cache invalidation');
      } else {
        console.log('ℹ️ No changes detected, cache preserved');
      }

      return {
        success: refreshResult.success,
        hadChanges: refreshResult.changes_detected,
        newTrades: refreshResult.trade_count || 0
      };

    } catch (error) {
      console.error('❌ Trade refresh failed:', error);
      
      // On error, we might want to invalidate cache to ensure fresh data on next load
      try {
        await invalidateTradeRelatedData('all', {
          strategy: 'conservative', // Conservative invalidation on error
          includeRelated: false,
          refetchActive: false
        });
      } catch (coordinatorError) {
        console.error('❌ Cache coordinator error invalidation failed, using fallback:', coordinatorError);
        await queryClient.invalidateQueries({ queryKey: ['closedTrades'] });
      }
      
      return {
        success: false,
        hadChanges: false,
        newTrades: 0
      };
    } finally {
      setIsFetching(false);
    }
  }, [isFetching, queryClient]);

  return {
    triggerSmartRefresh,
    isFetching,
    lastRefreshTime,
    refreshCount
  };
};
